import { CLIENT_CONTAINER } from '@lowcode/mcp-protocol/src/base/common';
import { BizLine } from '../types/rewardRecords';
import { appendParams, convertLinkToAppMiddleOpenLink } from './url';
/**
 * R007：酒旅跳转链接生成（通用基础能力，非任务专属）。
 * 参考 A-Gundam/hotel-super-group-rank-play/src/utils/getHotelUrl.ts：
 * 按「目标页」维度收敛 + 强类型入参 + 按端容器查模板表 + 单一入口。
 * 端策略：美团 app 直跳 imeituan:// scheme；其他端若无专属原生模板，统一经 H5 中间页唤端。
 */
/** 目标页（业务线 × 页面 拍平为一组目标页） */
export var TravelPageType;
(function (TravelPageType) {
    TravelPageType["HotelList"] = "HotelList";
    // 酒店 poi 列表
    TravelPageType["HotelDetail"] = "HotelDetail";
    // 酒店 poi 详情
    TravelPageType["HomeStayList"] = "HomeStayList";
    // 民宿 poi 列表
    TravelPageType["HomeStayDetail"] = "HomeStayDetail";
    // 民宿 poi 详情
    TravelPageType["TicketList"] = "TicketList";
    // 门票 poi 列表
    TravelPageType["VacationList"] = "VacationList";
    // 度假 poi 列表
    TravelPageType["FlightPrepose"] = "FlightPrepose";
    // 机票前置页
    TravelPageType["TrainPrepose"] = "TrainPrepose"; // 火车票前置页
})(TravelPageType || (TravelPageType = {}));
/**
 * 目标页 × 端容器 → URL 模板。
 * 美团 app（imeituan://）已确认；点评 app 有专属原生页的也单列（dianping://）；其余端缺省 → 由美团 app scheme 经中间页派生（见 getTravelUrl）。
 * 某端确有专属原生页时往对应端列加项即可（如点评 dianping://、小程序 /pages/…）。
 */
const TRAVEL_URL_TEMPLATE = {
    // 酒店列表页：来源 https://km.sankuai.com/collabpage/1984193567（前置页 imeituan://www.meituan.com/hotel/homepage 当前未路由，故不列）
    [TravelPageType.HotelList]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/hotel/list',
        [CLIENT_CONTAINER.DIANPING_APP]: 'dianping://hotelsearchresult'
    },
    // 酒店详情页：来源 https://km.sankuai.com/collabpage/316184155（最简跳链，key=id 值为 poiId；点评 shopinfo 需 shopid/shopuuid 我们无，故只给美团，非美团端经中间页唤端）
    [TravelPageType.HotelDetail]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/hotel/poi'
    },
    [TravelPageType.HomeStayList]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/mrn?mrn_biz=zhenguo&mrn_entry=filter&mrn_component=zhenguo-externalAd-filter&mrn_min_version=10.321.0&phx_wake_up_type=mtapp_entry&phx_wake_up_source=hotel_home_page_tab'
    },
    [TravelPageType.HomeStayDetail]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/mrn?mrn_biz=zhenguo&mrn_entry=product&mrn_component=zhenguo-product&mrn_translucent=true'
    },
    [TravelPageType.TicketList]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/htmrn?mrn_biz=travel&mrn_entry=travelpoilist&mrn_component=poilist'
    },
    // 度假列表页：来源 https://km.sankuai.com/collabpage/2696174882（基础跳链；仅拼 cityId，playTabId/query/isEmptySearch 等暂不传）
    [TravelPageType.VacationList]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/mrn?mrn_biz=travel&mrn_entry=grouplist-temp&mrn_component=grouplist-temp',
        [CLIENT_CONTAINER.DIANPING_APP]: 'dianping://mrn?mrn_biz=travel&mrn_entry=grouplist-temp&mrn_component=grouplist-temp'
    },
    [TravelPageType.FlightPrepose]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/htc/homepage?selectedTab=plane'
    },
    [TravelPageType.TrainPrepose]: {
        [CLIENT_CONTAINER.MEITUAN_APP]: 'imeituan://www.meituan.com/htc/homepage?selectedTab=train'
    }
};
/** R007：业务线 → 浏览/下单目标页（浏览酒店/民宿为详情页，需 poiId；其余浏览=下单同页） */
export const TRAVEL_PAGE_BY_BIZ = {
    [BizLine.Hotel]: {
        browse: TravelPageType.HotelDetail,
        order: TravelPageType.HotelList
    },
    [BizLine.HomeStay]: {
        browse: TravelPageType.HomeStayDetail,
        order: TravelPageType.HomeStayList
    },
    [BizLine.Ticket]: {
        browse: TravelPageType.TicketList,
        order: TravelPageType.TicketList
    },
    [BizLine.Vacation]: {
        browse: TravelPageType.VacationList,
        order: TravelPageType.VacationList
    },
    [BizLine.Air]: {
        browse: TravelPageType.FlightPrepose,
        order: TravelPageType.FlightPrepose
    },
    [BizLine.Train]: {
        browse: TravelPageType.TrainPrepose,
        order: TravelPageType.TrainPrepose
    }
};
/** 美团 app 容器（app 或 app webview）：直跳 scheme，无需中间页 */
function isMeituanAppContainer(container) {
    return container === CLIENT_CONTAINER.MEITUAN_APP || container === CLIENT_CONTAINER.MEITUAN_WEBVIEW;
}
/**
 * R007：生成酒旅跳转链接。
 * @param pageType 目标页
 * @param params   该目标页的强类型入参（key 即 query key）
 * @param container 当前端容器（默认美团 app）
 * @returns 跳转链接；该页无对应模板（path 未配）时返回空串，由调用方兜底（如 task.jumpUrl）
 */
export function getTravelUrl(pageType, params, container = CLIENT_CONTAINER.MEITUAN_APP) {
    const templates = TRAVEL_URL_TEMPLATE[pageType];
    const paramsRecord = (params ?? {});
    // 该端有专属原生模板（如点评 app / 小程序）→ 直接用
    const ownTemplate = templates?.[container];
    if (ownTemplate)
        return appendParams(ownTemplate, paramsRecord);
    // 否则用美团 app 模板
    const mtTemplate = templates?.[CLIENT_CONTAINER.MEITUAN_APP];
    if (!mtTemplate)
        return ''; // 该页美团 app path 未配 → 空串，调用方兜底
    const mtScheme = appendParams(mtTemplate, paramsRecord);
    // 美团 app 直跳；其他端经 H5 中间页唤端
    return isMeituanAppContainer(container) ? mtScheme : convertLinkToAppMiddleOpenLink(mtScheme);
}
